import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import { SmoothScroll } from "@/components/motion";
import { ThemeProvider } from "@/components/theme-provider";

// Lazy-loaded pages (Code Splitting)
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Services = lazy(() => import("./pages/Services"));
const AiSolutions = lazy(() => import("./pages/services/AiSolutions"));
const AiConsultation = lazy(() => import("./pages/services/AiConsultation"));
const AiTraining = lazy(() => import("./pages/services/AiTraining"));
const Kelioli = lazy(() => import("./pages/services/ai-solutions/Kelioli"));
const KelioliDemo = lazy(() => import("./pages/services/ai-solutions/KelioliDemo"));
const MatovizConsultation = lazy(() => import("./pages/services/ai-consultation/Matoviz"));
const FaqAiServices = lazy(() => import("./pages/faq/AiServices"));
const FaqAiConsultation = lazy(() => import("./pages/faq/AiConsultation"));
const FaqAiTraining = lazy(() => import("./pages/faq/AiTraining"));
const BlogAiServices = lazy(() => import("./pages/blog/AiServices"));
const BlogAiConsultation = lazy(() => import("./pages/blog/AiConsultation"));
const BlogAiTraining = lazy(() => import("./pages/blog/AiTraining"));

const queryClient = new QueryClient();

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Animated route outlet. Owns <Routes> and passes it an explicit `location`
 * snapshot so AnimatePresence can cross-fade between the outgoing and incoming
 * pages correctly (the standard framer-motion + React Router v6 pattern).
 */
const AnimatedRoutes = () => {
  const location = useLocation();

  // Reset scroll to the top on every route change.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35, ease: EASE }}
      >
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/ai-solutions" element={<AiSolutions />} />
          <Route path="/services/ai-solutions/kelioli" element={<Kelioli />} />
          <Route path="/services/ai-solutions/kelioli/demo" element={<KelioliDemo />} />
          <Route path="/services/ai-consultation" element={<AiConsultation />} />
          <Route path="/services/ai-consultation/matoviz" element={<MatovizConsultation />} />
          <Route path="/services/ai-training" element={<AiTraining />} />
          {/* Legacy aliases — keep old inbound links alive after the route move. */}
          <Route
            path="/services/ai-solutions/ai-call-monitor"
            element={<Navigate to="/services/ai-solutions/kelioli" replace />}
          />
          <Route
            path="/services/ai-solutions/ai-call-monitor/demo"
            element={<Navigate to="/services/ai-solutions/kelioli/demo" replace />}
          />
          <Route
            path="/services/ai-call-monitor"
            element={<Navigate to="/services/ai-solutions/kelioli" replace />}
          />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/faq/ai-services" element={<FaqAiServices />} />
          <Route path="/faq/ai-consultation" element={<FaqAiConsultation />} />
          <Route path="/faq/ai-training" element={<FaqAiTraining />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/ai-services" element={<BlogAiServices />} />
          <Route path="/blog/ai-consultation" element={<BlogAiConsultation />} />
          <Route path="/blog/ai-training" element={<BlogAiTraining />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

const App = () => (
  <ThemeProvider defaultTheme="light" storageKey="curigon-theme">
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SmoothScroll>
              <Suspense
                fallback={
                  <div className="min-h-screen bg-ink flex items-center justify-center">
                    <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                  </div>
                }
              >
                <AnimatedRoutes />
              </Suspense>
            </SmoothScroll>
          </BrowserRouter>
        </TooltipProvider>
      </MotionConfig>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
